import { Card, CircularProgress, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import SchoolIcon from '@mui/icons-material/School';
import { getRequest, postRequest, ResponseDataType } from 'api';
import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './home.module.css';
import { ROLE } from 'constant/role';
import {
  Table,
  TableProps,
  Select,
  Button,
  Modal,
  Space,
  Tooltip,
  Input,
  Tag,
  Empty,
} from 'antd';
import {
  SearchOutlined,
  CalendarOutlined,
  SyncOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { BillInfo } from 'Pages/Bill/Information';
import { EllipsisTooltip } from 'Pages/Bill/List/Tabs/EllipsisTooltip';
import { getStoredWorkMonth } from 'utils/workMonthStorage';

const { Option } = Select;

const card_sx = {
  color: '#141D49',
  padding: '10px',
  width: '250px',
  cursor: 'pointer',
};

const circular_progress_sx = {
  color: '#141D49',
};

interface GeneralInfoDataType {
  sumAccountants: number;
  sumTeachers: number;
  sumParents: number;
  sumStudents: number;
  sumSchools: number;
  sumInvoices: number;
  sumHomeroomClasses?: number;
  sumTeachingClasses?: number;
}

interface UnpaidBillItem {
  invoidId: string;
  billId?: string;
  studentInvoiceId: number;
  studentId: number;
  studentName: string;
  studentCode: string;
  className: string;
  classYearName?: string;
  parentPhonenum: string;
  cost: number;
  content: string;
  description: string;
  status: number;
  month: string;
  year: string;
  schoolYear: string;
}

const formatCurrency = (value: number): string => {
  return `${new Intl.NumberFormat('vi-VN').format(value || 0)} VNĐ`;
};

const months = [
  { label: 'Tháng 1', value: 1 },
  { label: 'Tháng 2', value: 2 },
  { label: 'Tháng 3', value: 3 },
  { label: 'Tháng 4', value: 4 },
  { label: 'Tháng 5', value: 5 },
  { label: 'Tháng 6', value: 6 },
  { label: 'Tháng 7', value: 7 },
  { label: 'Tháng 8', value: 8 },
  { label: 'Tháng 9', value: 9 },
  { label: 'Tháng 10', value: 10 },
  { label: 'Tháng 11', value: 11 },
  { label: 'Tháng 12', value: 12 },
];

export const Home = (): JSX.Element | null => {
  const [generalInfo, setGeneralInfo] = useState<GeneralInfoDataType | null>(null);
  const schoolId = localStorage.getItem('schoolId');
  const role = localStorage.getItem('role');

  const isAdmin = role === ROLE.ADMIN;
  const isAccountant = role === ROLE.ACCOUNTANT;
  const isTeacher = role === ROLE.TEACHER;

  // State cho chức năng tra cứu biên lai chờ thanh toán theo SĐT Phụ huynh
  const [selectedYear, setSelectedYear] = useState<number>(() => getStoredWorkMonth().year);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [searchedPhone, setSearchedPhone] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [billList, setBillList] = useState<UnpaidBillItem[]>([]);
  const [loadingBills, setLoadingBills] = useState<boolean>(false);
  const [payingCash, setPayingCash] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [selectedStudentInvoiceId, setSelectedStudentInvoiceId] = useState<number | undefined>();
  const [hasDetailChanged, setHasDetailChanged] = useState<boolean>(false);

  const years = useMemo(() => {
    const startYear = 2021;
    const endYear = Math.max(new Date().getFullYear() + 2, selectedYear + 1);
    const list: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      list.push(y);
    }
    return list;
  }, [selectedYear]);

  const fetchHomeSummary = useCallback(async () => {
    const queryParams = new URLSearchParams({ schoolId: schoolId || '' }).toString();
    if (isAdmin) {
      const response: ResponseDataType<GeneralInfoDataType> = await getRequest(`/cms/customer/home?${queryParams}`);
      if (response?.code === 200 && response.info) {
        setGeneralInfo(response.info);
      }
    } else if (isAccountant) {
      const response: ResponseDataType<GeneralInfoDataType> = await getRequest(`/cms/invoice/home?${queryParams}`);
      if (response?.code === 200 && response.info) {
        setGeneralInfo(response.info);
      }
    } else if (isTeacher) {
      setGeneralInfo({
        sumAccountants: 0,
        sumTeachers: 0,
        sumParents: 0,
        sumStudents: 0,
        sumSchools: 0,
        sumInvoices: 0,
        sumHomeroomClasses: 1,
        sumTeachingClasses: 1,
      });
    }
  }, [isAdmin, isAccountant, isTeacher, schoolId]);

  useEffect(() => {
    fetchHomeSummary();
  }, [fetchHomeSummary]);

  // Đồng bộ Kỳ làm việc khi thay đổi từ cấu hình
  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      if (e.detail) {
        setSelectedMonth(e.detail.month);
        setSelectedYear(e.detail.year);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  const searchBillsByPhone = useCallback(
    async (phoneToSearch: string, monthVal: number, yearVal: number) => {
      const trimmed = phoneToSearch.trim();
      if (!trimmed) {
        setHasSearched(false);
        setSearchedPhone('');
        setBillList([]);
        setSelectedRowKeys([]);
        return;
      }

      setLoadingBills(true);
      setHasSearched(true);
      setSearchedPhone(trimmed);

      try {
        const queryString = new URLSearchParams({
          schoolId: schoolId || '',
          month: monthVal.toString(),
          year: yearVal.toString(),
          phone: trimmed,
        }).toString();

        const res: ResponseDataType<{ list: UnpaidBillItem[]; total: number }> = await getRequest(
          `/cms/invoice/search-unpaid-by-phone?${queryString}`
        );

        if (res?.code === 200 && res.info?.list) {
          const list = res.info.list;
          setBillList(list);
          // Tự động chọn sẵn các biên lai tìm được để kế toán thao tác nhanh
          setSelectedRowKeys(list.map((item) => item.studentInvoiceId));
        } else {
          setBillList([]);
          setSelectedRowKeys([]);
        }
      } catch (error) {
        console.error('Error searching unpaid bills by phone:', error);
        toast.error('Lỗi khi tra cứu biên lai theo số điện thoại');
        setBillList([]);
        setSelectedRowKeys([]);
      } finally {
        setLoadingBills(false);
      }
    },
    [schoolId]
  );

  // Tự động tra cứu lại nếu đổi Tháng/Năm khi đang có SĐT đã tìm kiếm
  useEffect(() => {
    if (hasSearched && searchedPhone) {
      searchBillsByPhone(searchedPhone, selectedMonth, selectedYear);
    }
  }, [selectedMonth, selectedYear, hasSearched, searchedPhone, searchBillsByPhone]);

  const handleSearchTrigger = () => {
    const trimmed = phoneInput.trim();
    if (!trimmed) {
      setHasSearched(false);
      setSearchedPhone('');
      setBillList([]);
      setSelectedRowKeys([]);
      toast.warning('Vui lòng nhập số điện thoại phụ huynh để tìm kiếm!');
      return;
    }
    searchBillsByPhone(trimmed, selectedMonth, selectedYear);
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPhoneInput(val);
    // Khi xóa trắng ô nhập -> ẩn toàn bộ biên lai ngay lập tức
    if (!val.trim()) {
      setHasSearched(false);
      setSearchedPhone('');
      setBillList([]);
      setSelectedRowKeys([]);
    }
  };

  const selectedBills = useMemo(() => {
    const keySet = new Set(selectedRowKeys.map(Number));
    return billList.filter((b) => keySet.has(b.studentInvoiceId));
  }, [billList, selectedRowKeys]);

  const totalSelectedAmount = useMemo(() => {
    return selectedBills.reduce((sum, item) => sum + (item.cost || 0), 0);
  }, [selectedBills]);

  const confirmCashPayment = (invoiceIds: number[], totalCost: number, labelInfo?: string) => {
    if (!invoiceIds.length) {
      toast.warning('Vui lòng tích chọn ít nhất một biên lai để thanh toán!');
      return;
    }

    Modal.confirm({
      title: 'Xác nhận thanh toán bằng tiền mặt',
      content: (
        <div style={{ marginTop: 8 }}>
          <p style={{ marginBottom: 6 }}>
            Bạn có chắc chắn muốn xác nhận đã thu <strong>tiền mặt</strong> cho{' '}
            {labelInfo ? (
              <strong>{labelInfo}</strong>
            ) : (
              <>
                <strong>{invoiceIds.length}</strong> biên lai đã chọn
              </>
            )}
            ?
          </p>
          <div
            style={{
              padding: '10px 12px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            Tổng số tiền mặt thực thu:{' '}
            <strong style={{ color: '#cf1322', fontSize: 15 }}>
              {formatCurrency(totalCost)}
            </strong>
          </div>
        </div>
      ),
      okText: 'Xác nhận đã thu tiền',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setPayingCash(true);
          const res: any = await postRequest(
            '/cms/invoice/cash-payment-batch',
            { studentInvoiceIds: invoiceIds },
            'json'
          );
          if (res?.code === 200) {
            toast.success(res?.msg || 'Xác nhận thanh toán tiền mặt thành công!');
            await searchBillsByPhone(searchedPhone, selectedMonth, selectedYear);
            fetchHomeSummary();
          } else {
            toast.error(res?.msg || 'Không thể xác nhận thanh toán tiền mặt');
          }
        } catch (error) {
          console.error('Error batch cash payment:', error);
          toast.error('Lỗi khi gọi API thanh toán tiền mặt');
        } finally {
          setPayingCash(false);
        }
      },
    });
  };

  const columns: TableProps<UnpaidBillItem>['columns'] = [
    {
      title: 'Mã biên lai',
      dataIndex: 'invoidId',
      key: 'invoidId',
      align: 'center',
      width: 135,
      ellipsis: { showTitle: false },
      render: (text: string, record: UnpaidBillItem) => (
        <EllipsisTooltip text={text || record.billId || ''} />
      ),
    },
    {
      title: 'CCCD / Mã HS',
      dataIndex: 'studentCode',
      key: 'studentCode',
      align: 'center',
      width: 130,
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text || '—'} />,
    },
    {
      title: 'Tên học sinh',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'left',
      width: 175,
      ellipsis: { showTitle: false },
      render: (text: string) => <EllipsisTooltip text={text || ''} />,
    },
    {
      title: 'Lớp học',
      dataIndex: 'className',
      key: 'className',
      align: 'center',
      width: 95,
      ellipsis: { showTitle: false },
      render: (_: any, record: UnpaidBillItem) => (
        <EllipsisTooltip text={record.className || record.classYearName || '—'} />
      ),
    },
    {
      title: 'SĐT Phụ huynh',
      dataIndex: 'parentPhonenum',
      key: 'parentPhonenum',
      align: 'center',
      width: 125,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text || searchedPhone}</span>,
    },
    {
      title: 'Kỳ thu',
      key: 'period',
      align: 'center',
      width: 95,
      render: (_: any, record: UnpaidBillItem) => (
        <Tag color="blue" style={{ margin: 0 }}>
          T{String(record.month || selectedMonth).padStart(2, '0')}/{record.year || selectedYear}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'cost',
      key: 'cost',
      align: 'right',
      width: 130,
      render: (cost: number) => (
        <span style={{ fontWeight: 700, color: '#B9392E' }}>{formatCurrency(cost)}</span>
      ),
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      align: 'left',
      ellipsis: { showTitle: false },
      render: (content: string) => <EllipsisTooltip text={content || ''} />,
    },
    {
      title: 'Hành động',
      key: 'action',
      align: 'center',
      width: 175,
      render: (_: any, record: UnpaidBillItem) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() =>
              confirmCashPayment(
                [record.studentInvoiceId],
                record.cost,
                `học sinh ${record.studentName} (${record.invoidId || record.studentInvoiceId})`
              )
            }
          >
            Thu tiền
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedStudentInvoiceId(record.studentInvoiceId);
              setHasDetailChanged(false);
              setIsDetailModalOpen(true);
            }}
          >
            Chi tiết
          </Button>
        </Space>
      ),
    },
  ];

  if (isAdmin) {
    return (
      <Grid container spacing={5}>
        <Grid item>
          <Link style={{ textDecoration: 'none' }} to=''>
            <Card sx={card_sx}>
              <p className={styles.card_title}>Kế toán</p>
              <p className={styles.card_number}>
                {generalInfo ? generalInfo.sumAccountants : <CircularProgress sx={circular_progress_sx} />}
              </p>
              <Grid container justifyContent='flex-end'>
                <DomainOutlinedIcon />
              </Grid>
            </Card>
          </Link>
        </Grid>
        <Grid item>
          <Link style={{ textDecoration: 'none' }} to=''>
            <Card sx={card_sx}>
              <p className={styles.card_title}>Giáo viên</p>
              <p className={styles.card_number}>
                {generalInfo ? generalInfo.sumTeachers : <CircularProgress sx={circular_progress_sx} />}
              </p>
              <Grid container justifyContent='flex-end'>
                <PeopleAltOutlinedIcon />
              </Grid>
            </Card>
          </Link>
        </Grid>
        <Grid item>
          <Link style={{ textDecoration: 'none' }} to='/'>
            <Card sx={card_sx}>
              <p className={styles.card_title}>Phụ huynh</p>
              <p className={styles.card_number}>
                {generalInfo ? generalInfo.sumParents : <CircularProgress sx={circular_progress_sx} />}
              </p>
              <Grid container justifyContent='flex-end'>
                <PeopleAltOutlinedIcon />
              </Grid>
            </Card>
          </Link>
        </Grid>
        <Grid item>
          <Link style={{ textDecoration: 'none' }} to='/'>
            <Card sx={card_sx}>
              <p className={styles.card_title}>Học sinh</p>
              <p className={styles.card_number}>
                {generalInfo ? generalInfo.sumStudents : <CircularProgress sx={circular_progress_sx} />}
              </p>
              <Grid container justifyContent='flex-end'>
                <PeopleAltOutlinedIcon />
              </Grid>
            </Card>
          </Link>
        </Grid>
      </Grid>
    );
  }

  if (isAccountant) {
    const isWorkingPeriod =
      selectedMonth === getStoredWorkMonth().month && selectedYear === getStoredWorkMonth().year;

    return (
      <div style={{ padding: '4px 16px 24px 16px' }}>
        {/* Hàng thống kê tổng quan gọn gàng */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Link style={{ textDecoration: 'none' }} to="/bill-list">
            <Card
              sx={{
                color: '#141D49',
                px: 2.5,
                py: 1.5,
                minWidth: 220,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748B' }}>
                  Biên lai chờ thanh toán
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#B9392E', lineHeight: 1.2, marginTop: 4 }}>
                  {generalInfo ? generalInfo.sumInvoices : <CircularProgress size={22} sx={circular_progress_sx} />}
                </div>
              </div>
              <DomainOutlinedIcon sx={{ fontSize: 32, color: '#94A3B8' }} />
            </Card>
          </Link>
        </div>

        {/* Khối Tra cứu & Thu tiền mặt theo SĐT Phụ huynh */}
        <Card
          sx={{
            p: 2.5,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            borderRadius: 2,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#141D49' }}>
                Tra cứu & Thu tiền mặt theo SĐT Phụ huynh
              </h3>
              <span style={{ fontSize: 13, color: '#64748B' }}>
                Nhập số điện thoại phụ huynh để tìm các biên lai đang chờ thanh toán trong kỳ làm việc
              </span>
            </div>

            {/* Bộ chọn Kỳ làm việc */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Select
                placeholder="Năm"
                value={selectedYear}
                onChange={(value) => setSelectedYear(value)}
                style={{ width: 105 }}
              >
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Tháng"
                value={selectedMonth}
                onChange={(value) => setSelectedMonth(value)}
                style={{ width: 115 }}
              >
                {months.map((month) => (
                  <Option key={month.value} value={month.value}>
                    {month.label}
                  </Option>
                ))}
              </Select>
              {isWorkingPeriod ? (
                <Tag
                  color="blue"
                  style={{
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontWeight: 500,
                    margin: 0,
                  }}
                >
                  <CalendarOutlined /> Kỳ làm việc
                </Tag>
              ) : (
                <Tooltip title="Đang xem kỳ khác. Bấm để quay về Kỳ làm việc đã cấu hình">
                  <Button
                    size="middle"
                    icon={<SyncOutlined />}
                    onClick={() => {
                      const wm = getStoredWorkMonth();
                      setSelectedMonth(wm.month);
                      setSelectedYear(wm.year);
                    }}
                  >
                    Về kỳ T{getStoredWorkMonth().month}/{getStoredWorkMonth().year}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Thanh nhập SĐT Phụ huynh */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              padding: '12px 16px',
              background: '#F8FAFC',
              borderRadius: 8,
              border: '1px solid #E2E8F0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 280, maxWidth: 520 }}>
              <Input
                size="large"
                placeholder="Nhập số điện thoại phụ huynh (VD: 0912345678)..."
                prefix={<PhoneOutlined style={{ color: '#64748B' }} />}
                allowClear
                value={phoneInput}
                onChange={handlePhoneInputChange}
                onPressEnter={handleSearchTrigger}
              />
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                loading={loadingBills}
                onClick={handleSearchTrigger}
              >
                Tìm kiếm
              </Button>
            </div>

            {/* Thanh tổng tiền & nút Xác nhận thanh toán tiền mặt (chỉ hiện khi có kết quả) */}
            {hasSearched && billList.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 14, color: '#334155' }}>
                  Đã chọn: <strong>{selectedRowKeys.length}</strong> / {billList.length} biên lai &nbsp;|&nbsp; Tổng thu:{' '}
                  <strong style={{ fontSize: 16, color: '#B9392E' }}>
                    {formatCurrency(totalSelectedAmount)}
                  </strong>
                </div>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  disabled={selectedRowKeys.length === 0}
                  loading={payingCash}
                  onClick={() => confirmCashPayment(selectedRowKeys.map(Number), totalSelectedAmount)}
                  style={{ background: '#16a34a', borderColor: '#16a34a', fontWeight: 600 }}
                >
                  Đã thanh toán bằng tiền mặt ({selectedRowKeys.length})
                </Button>
              </div>
            )}
          </div>

          {/* Khu vực hiển thị kết quả: Chỉ hiển thị sau khi đã nhập SĐT và bấm Tìm kiếm */}
          {hasSearched && searchedPhone ? (
            <div style={{ marginTop: 16 }}>
              <Table
                tableLayout="fixed"
                loading={loadingBills}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                }}
                columns={columns}
                dataSource={billList}
                rowKey="studentInvoiceId"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span>
                          Không tìm thấy biên lai chờ thanh toán nào cho SĐT{' '}
                          <strong>{searchedPhone}</strong> trong kỳ{' '}
                          <strong>
                            T{String(selectedMonth).padStart(2, '0')}/{selectedYear}
                          </strong>
                        </span>
                      }
                    />
                  ),
                }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                }}
              />
            </div>
          ) : null}
        </Card>

        {/* Modal xem chi tiết biên lai */}
        <Modal
          title="Chi tiết biên lai"
          open={isDetailModalOpen}
          onCancel={() => {
            setIsDetailModalOpen(false);
            setSelectedStudentInvoiceId(undefined);
            if (hasDetailChanged && searchedPhone) {
              searchBillsByPhone(searchedPhone, selectedMonth, selectedYear);
              fetchHomeSummary();
              setHasDetailChanged(false);
            }
          }}
          footer={null}
          width={1500}
          destroyOnClose
        >
          <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {selectedStudentInvoiceId && (
              <BillInfo
                studentInvoiceId={selectedStudentInvoiceId}
                onSuccess={() => setHasDetailChanged(true)}
              />
            )}
          </div>
        </Modal>
      </div>
    );
  }

  if (isTeacher) {
    return (
      <Grid container spacing={5}>
        <Grid item>
          <Card sx={card_sx}>
            <p className={styles.card_title}>Lớp chủ nhiệm</p>
            <p className={styles.card_number}>
              {generalInfo ? generalInfo.sumHomeroomClasses : <CircularProgress sx={circular_progress_sx} />}
            </p>
            <Grid container justifyContent='flex-end'>
              <SchoolIcon />
            </Grid>
          </Card>
        </Grid>
        <Grid item>
          <Card sx={card_sx}>
            <p className={styles.card_title}>Lớp dạy</p>
            <p className={styles.card_number}>
              {generalInfo ? generalInfo.sumTeachingClasses : <CircularProgress sx={circular_progress_sx} />}
            </p>
            <Grid container justifyContent='flex-end'>
              <SchoolIcon />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return null;
};
